import bemify from '../../../utils/bemUtils';
import TextFieldType from './textFieldType';
import Component from '../../component/component';

/**
 * Represents a text field component with a consistent look and feel across browsers.
 */
class TextField extends Component {
  #textFieldRef = null;
  // Indicates that this field can be associated
  // with a form and recieve focus if a label is
  // provided and clicked for the field.
  static formAssociated = true;

  /**
   * Gets the default value of the component.
   */
  get defaultValue() {
    return this.getAttribute('default-value');
  }

  /**
   * Gets a value that determines whether the component is disabled.
   */
  get disabled() {
    return this.hasAttribute('disabled');
  }

  /**
   * Sets a value that determines whether the component is disabled.
   */
  set disabled(value) {
    if (value) {
      this.setAttribute('disabled', '');
    } else {
      this.removeAttribute('disabled');
    }
  }

  /**
   * Gets a value that determines whether the component is a multiline text field.
   */
  get multiline() {
    return this.hasAttribute('multiline');
  }

  /**
   * Gets the placeholder text to show if no item has been selected.
   */
  get placeholder() {
    return this.getAttribute('placeholder');
  }

  /**
   * Gets a value that determines whether the component is readonly.
   * @remarks Often used with multiline.
   */
  get readonly() {
    return this.hasAttribute('readonly');
  }

  /**
   * Gets the type of text field.
   */
  get type() {
    return this.getAttribute('type') ?? TextFieldType.text;
  }

  initTemplate() {
    const [block] = bemify('text-field');
    const template = this.createTemplate();

    if (this.multiline) {
      template.innerHTML += `
      <textarea
        ${this.addAttributeMarkup('id', this.id)}
        class="${block(this.multiline ? 'multiline' : '')}"
        ${this.disabled ? this.addAttributeMarkup('disabled', '', false) : ''}
        part="input"
        ${this.addAttributeMarkup('name', this.name)}
        ${this.addAttributeMarkup('placeholder', this.placeholder)}
        ${this.readonly ? this.addAttributeMarkup('readonly', '', false) : ''}>${this.defaultValue ?? ''}</textarea>
    `;
    } else {
      template.innerHTML += `
      <input
        ${this.addAttributeMarkup('id', this.id)}
        class="${block()}"
        ${this.disabled ? this.addAttributeMarkup('disabled', '', false) : ''}
        ${this.addAttributeMarkup('name', this.name)}
        part="input"
        ${this.addAttributeMarkup('placeholder', this.placeholder)}
        type="${this.type}"
        ${this.addAttributeMarkup('value', this.defaultValue)} />
    `;
    }

    this.initShadowRoot(template.content.cloneNode(true), true);
  }

  onMounted() {
    this.#textFieldRef = this.shadowRoot?.querySelector('[part="input"]');
    this.#textFieldRef?.addEventListener('keydown', e => this.fireCustomEvent('on-keydown', { e, target: e.target }));
    this.#textFieldRef?.addEventListener('input', e => this.fireCustomEvent('on-change', { target: e.target }));
    this.#textFieldRef?.addEventListener('blur', e => this.fireCustomEvent('on-blur', { target: e.target }));
  }

  /**
   * Called once to return the attributes to observe.
   */
  static get observedAttributes() {
    return ['disabled'];
  }

  onAttributeChange() {
    if (this.disabled) {
      this.#textFieldRef?.setAttribute('disabled', '');
      return;
    }

    this.#textFieldRef?.removeAttribute('disabled');
  }
}

customElements.define('sk-text-field', TextField);

export default TextField;
